/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {AbstractFilterStateModel} from './abstract-filter-state.model';
import {SortDirection} from '../../api/generic/contracts/sort-direction.enum';
import {DateCriterionModel} from '../../api/generic/contracts/date-criterion.model';
import {DateFilterStateModel} from './date-filter/date-filter-state.model';
import {CodeFilterStateModel} from './code-filter/code-filter-state.model';
import {NullableStringCriterionModel} from '../../api/generic/contracts/nullable-string-criterion.model';
import {InputAndCodeFilterStateModel} from './input-and-code-filter/input-and-code-filter-state.model';
import {QuantityCriterionModel} from '../../api/generic/contracts/quantity-criterion.model';
import {NumberFilterOptionEnum} from './number-filter/number-filter-option.enum';
import {NumberFilterOperatorEnum} from './number-filter/number-filter-operator.enum';
import {NullableDateCriterionModel} from '../../api/generic/contracts/nullable-date-criterion.model';
import {NullableStringListCriterionModel} from '../../api/generic/contracts/nullable-string-list-criterion.model';
import {PaginationCriteriaModel} from '../../api/generic/contracts/pagination-criteria.model';
import {SpoListFilterColumnEnum} from '../../spo/spo-main-page/spo-list/spo-list-filter-column.enum';
import {ClaimListFilterColumnEnum} from '../../claim/claim-main-page/claim-list/claim-list-filter-column.enum';
import {CodeFilterModel} from './code-filter/code-filter.model';
import {InputFilterStateModel} from './input-filter/input-filter-state.model';
import {
    BusinessSettingsVersionColumnEnum
} from '../../settings/business-settings/models/business-settings-version-column.enum';
import {Injectable} from '@angular/core';
import {PartNumberChangesColumnEnum} from '../../part-number-changes/models/part-number-changes-column.enum';
import {
    UserTableFilterColumnEnum
} from '../../settings/roles-management/users-table/models/user-table-filter-column.enum';
import {SettlementColumnEnum} from '../../settlements/models/settlement-column.enum';
import {NumberFilterStateModel} from './number-filter/number-filter-state.model';
import {PeriodsTableColumnEnum} from '../../bundles/periods-table/models/periods-table-column.enum';
import clone from 'clone';
import {PeriodBundlesTableColumnEnum} from '../../bundles/period-bundles-table/models/period-bundles-table-column.enum';
import {AlertsColumnEnum} from '../../settings/alerts/alerts-main-page/models/alerts-column.enum';

// This doesn't really enforce using enums on T, but at least we ensure type safety when using a property of type T
type TEnum = SpoListFilterColumnEnum
        | ClaimListFilterColumnEnum
        | BusinessSettingsVersionColumnEnum
        | PartNumberChangesColumnEnum
        | UserTableFilterColumnEnum
        | SettlementColumnEnum
        | PeriodsTableColumnEnum
        | PeriodBundlesTableColumnEnum
        | AlertsColumnEnum;

@Injectable()
export abstract class AbstractSearchCriteriaService<T extends TEnum> {
    public stateMap = new Map<T, AbstractFilterStateModel>();
    public sortDirection: SortDirection;
    public sortedBy: T;
    public pageSize = 20;
    public pageNumber = 1;
    public totalResults = 0;

    protected constructor() {
        this.clear();
    }

    /**
     * Check if the filter state has changed and save value when changed.
     * Return true if the new status is active and is different from previous (SEARCH) or if the new one is inactive and old one its active
     * (CLEAR).
     * */
    public setState(filter: T, state: AbstractFilterStateModel, updateView: Function): void {
        if (this.hasFilterChanged(state.filterName as T, state)) {
            this.stateMap.set(state.filterName as T, clone(state));
            updateView();
        }
    }

    get resetPaginationCriteria(): PaginationCriteriaModel {
        this.pageNumber = 1;
        return {
            sortCriteria: [
                {
                    criterion: this.sortedBy,
                    sortDirection: this.sortDirection
                }
            ],
            firstResult: 0,
            maxResults: this.pageSize
        };
    }

    protected get paginationCriteria(): PaginationCriteriaModel {
        return {
            sortCriteria: [
                {
                    criterion: this.sortedBy,
                    sortDirection: this.sortDirection
                }
            ],
            firstResult: this.pageSize * (this.pageNumber - 1),
            maxResults: this.pageSize
        };
    }

    protected mapDateCriterion(filter: T): DateCriterionModel {
        let criterion: DateCriterionModel;

        const stateModel = this.stateMap.get(filter) as DateFilterStateModel;

        if (stateModel != null) {
            criterion = {
                startDate: stateModel.start,
                endDate: stateModel.end
            };
        }

        return criterion;
    }

    protected mapNullableDateCriterion(filter: T): NullableDateCriterionModel {
        let criterion: NullableDateCriterionModel;

        const stateModel = this.stateMap.get(filter) as DateFilterStateModel;

        if (stateModel != null) {
            criterion = {
                startDate: stateModel.start,
                endDate: stateModel.end,
                empty: stateModel.empty
            };
        }

        return criterion;
    }

    protected mapCodeCriterion(filter: T): string[] {
        let criterion: string[];

        const stateModel = this.stateMap.get(filter) as CodeFilterStateModel;

        if (stateModel != null && stateModel.isActive) {
            criterion = this.flattenCodeTree(stateModel.filteredCodes)
                    .filter((code) => code.checked)
                    .map((code) => code.code);
        }

        return criterion;
    }

    protected mapStringCriterion(filter: T): string {
        let criterion: string;

        const stateModel = this.stateMap.get(filter) as InputFilterStateModel;

        if (stateModel != null) {
            criterion = stateModel.searchText;
        }

        return criterion;
    }

    protected mapNullableStringListCriterion(filter: T): NullableStringListCriterionModel {
        let criterion: NullableStringListCriterionModel;

        const stateModel = this.stateMap.get(filter) as CodeFilterStateModel;

        if (stateModel != null && stateModel.isActive) {
            criterion = {
                values: this.flattenCodeTree(stateModel.filteredCodes)
                        .filter((code) => code.checked)
                        .map((code) => code.code)
            };

            if (!stateModel.all && stateModel.empty) {
                criterion.empty = true;
            }
        }

        return criterion;
    }

    protected mapNullableStringCriterion(filter: T): NullableStringCriterionModel {
        let criterion: NullableStringCriterionModel;

        const stateModel = this.stateMap.get(filter) as InputAndCodeFilterStateModel;

        if (stateModel != null) {
            criterion = {
                value: stateModel.searchText
            };

            if (!stateModel.all && (stateModel.empty || stateModel.searchText == null || stateModel.searchText.length === 0)) {
                criterion.empty = stateModel.empty;
            }
        }

        return criterion;
    }

    protected mapDecimalQuantityCriterion(filter: T): QuantityCriterionModel {
        return this.mapQuantityCriterion(filter, 1e-6);
    }

    protected mapQuantityCriterion(filter: T, intervalSize = 1): QuantityCriterionModel {
        let criterion: QuantityCriterionModel;

        const stateModel = this.stateMap.get(filter) as NumberFilterStateModel;

        if (stateModel != null && stateModel.operator != null && stateModel.option === NumberFilterOptionEnum.CHOOSE) {
            const defaultValue = parseFloat(stateModel.defaultValue);
            const extraValue = parseFloat(stateModel.extraValue);

            switch (stateModel.operator.value) {
                case NumberFilterOperatorEnum.EQUAL:
                    criterion = {
                        biggerThan: defaultValue,
                        lesserThan: defaultValue
                    };
                    break;
                case NumberFilterOperatorEnum.BIGGER_THAN:
                    criterion = {
                        biggerThan: defaultValue + intervalSize,
                        lesserThan: null
                    };
                    break;
                case NumberFilterOperatorEnum.EQUAL_BIGGER:
                    criterion = {
                        biggerThan: defaultValue,
                        lesserThan: null
                    };
                    break;
                case NumberFilterOperatorEnum.SMALLER_THAN:
                    criterion = {
                        biggerThan: null,
                        lesserThan: defaultValue - intervalSize
                    };
                    break;
                case NumberFilterOperatorEnum.EQUAL_SMALLER:
                    criterion = {
                        biggerThan: null,
                        lesserThan: defaultValue
                    };
                    break;
                case NumberFilterOperatorEnum.BETWEEN:
                    criterion = {
                        biggerThan: defaultValue + intervalSize,
                        lesserThan: extraValue - intervalSize
                    };
                    break;
            }
        }

        return criterion;
    }

    private flattenCodeTree(codes: CodeFilterModel[]): CodeFilterModel[] {
        const flatCodes: CodeFilterModel[] = [];

        if (codes != null) {
            codes.forEach((code) => {
                if (code.children == null) {
                    flatCodes.push(code);
                } else {
                    flatCodes.push(...this.flattenCodeTree(code.children));
                }
            });
        }

        return flatCodes;
    }

    private hasFilterChanged(filter: T, state: AbstractFilterStateModel): boolean {
        return (state.isActive
                        && JSON.stringify(this.stateMap.get(filter), this.circularFunc())
                        !== JSON.stringify(clone(state), this.circularFunc()))
                || (!state.isActive && this.stateMap.get(filter) != null && this.stateMap.get(filter).isActive);
    };

    // This is an auxiliary function for JSON.stringify to remove circular dependencies when a filter has parents inside their child.
    private circularFunc(): any {
        const sited = new WeakSet();
        return (key: any, value: object): any => {
            if (typeof value === 'object' && value !== null) {
                if (sited.has(value)) {
                    return undefined;
                }
                sited.add(value);
            }
            return value;
        };
    }

    abstract clear(): void;
}
